import { CheckCircle2, X, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SubscriptionButton } from '@/components/subscription-button';

interface MobilePricingCardProps {
  isOpen: boolean;
  onClose: () => void;
  onViewAllPlans: () => void;
  product: {
    name: string;
    description: string;
    price: number;
    priceId: string;
    features: string[];
    credits: number;
    popular?: boolean;
    mode?: 'payment' | 'subscription';
  };
}

export function MobilePricingCard({
  isOpen,
  onClose,
  onViewAllPlans,
  product
}: MobilePricingCardProps) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-sm rounded-2xl bg-card border border-purple-500/30 overflow-hidden"
      >
        {/* Popular badge */}
        {product.popular && (
          <div className="absolute top-0 left-0 right-0 flex justify-center">
            <Badge className="mt-1 bg-purple-600 text-white px-3 py-1 text-xs">
              인기 상품
            </Badge>
          </div>
        )}
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-2 top-2 rounded-full bg-black/40 p-1.5 text-white backdrop-blur-md transition-colors hover:bg-black/60"
        >
          <X className="h-4 w-4" />
        </button>
        
        {/* Product header */}
        <div className="bg-gradient-to-br from-purple-900/60 to-purple-800/20 p-6 pt-8">
          <h3 className="text-xl font-bold text-card-foreground">{product.name}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{product.description}</p>
          
          <div className="mt-4 flex items-baseline">
            <span className="text-3xl font-bold text-card-foreground">
              {new Intl.NumberFormat('ko-KR').format(product.price)}
            </span>
            <span className="ml-1 text-sm text-muted-foreground">원/주</span>
          </div>
        </div>
        
        {/* Credits highlight */}
        <div className="flex items-center gap-2 m-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
          <div className="rounded-full bg-primary/20 p-1.5">
            <Zap className="h-4 w-4 text-primary" />
          </div>
          <span className="font-medium text-primary">
           +            {new Intl.NumberFormat('ko-KR').format(product.credits)} 크레딧

          </span>
        </div>
        
        {/* Features list */}
        <div className="px-4 pb-2">
          <ul className="space-y-2.5 mb-4">
            {product.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Action buttons */}
        <div className="p-4 pt-2 space-y-3">
          <SubscriptionButton 
            priceId={product.priceId} 
            mode={product.mode || 'subscription'}
            className="gradient-button h-12 font-medium"
          >
          
            결제하고 바로 채팅하기
          </SubscriptionButton>
          
          <Button 
            variant="ghost" 
            onClick={onViewAllPlans}
            className="w-full h-10 text-muted-foreground p-4 font-normal"
          >
            모든 요금제 보기
          </Button>
        </div>
      </motion.div>
    </div>
  );
}